import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, X, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const SEEN_KEY = "jv_module_trial_prompt_seen";

// One-time popup (once per browser) inviting non-subscribers to start the 1-month free trial.
export const ModuleTrialPrompt = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Only for non-subscribers (guests or users still on the free plan).
    const isSubscriber = user && user.plan && user.plan !== "free";
    if (isSubscriber) return;
    if (localStorage.getItem(SEEN_KEY)) return;
    const t = setTimeout(() => {
      localStorage.setItem(SEEN_KEY, "1");
      setOpen(true);
    }, 900);
    return () => clearTimeout(t);
  }, [user]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="module-trial-prompt"
      onClick={() => setOpen(false)}
    >
      <div className="glass-card rounded-3xl max-w-md w-full p-8 relative" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={() => setOpen(false)}
          data-testid="module-trial-close"
          className="absolute top-4 right-4 text-inkmuted hover:text-ink"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="w-12 h-12 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center">
          <Sparkles className="w-6 h-6 text-gold" />
        </div>
        <h3 className="font-serif text-3xl text-ink mt-5">Try JusticeVerse free for 1 month</h3>
        <p className="text-inkmuted text-sm mt-3">
          Unlock every premium module — JusticeVerse AI, Counsel AI, CaseBrief, PrepMate & more — free for 30 days.
          No money charged today. Cancel anytime.
        </p>
        <ul className="space-y-2.5 mt-5">
          {["Full access from day one", "₹0 charged for 30 days", "Autopay begins only after the trial", "Cancel anytime — instantly"].map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-sm text-inkmuted">
              <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /> {f}
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-3 mt-6">
          <button
            onClick={() => setOpen(false)}
            data-testid="module-trial-later"
            className="text-inkmuted hover:text-ink text-sm px-4 py-3"
          >
            Maybe later
          </button>
          <button
            onClick={() => { setOpen(false); navigate(user ? "/pricing" : "/register"); }}
            data-testid="module-trial-cta"
            className="flex-1 rounded-full bg-btn text-btnink hover:bg-btnhover px-6 py-3.5 text-sm font-semibold uppercase tracking-wider transition-all"
          >
            Start 1-Month Free Trial
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModuleTrialPrompt;
